import React from "react";
import { useNavigate, Link, useParams } from "react-router-dom";

const ThreePHelp = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-black text-white [&_p]:text-white min-h-screen p-4 mt-2">

      <div className="mx-auto">

        {/* Header */}
        <h1 className="text-lg lg:text-xl font-bold text-yellow-200 mb-2">
          🧭 Think5Whys PTM 3P – Complete User Guide
        </h1>

        <h1 className="text-sm lg:text-lg text-gray-100 mb-6">
          Practical Thinking Mathematics – Three Point Estimation Platform
        </h1>

        <div className="flex justify-center">
          <button
            className="ml-2 h-16 lg:h-10 px-1 text-xs lg:text-sm w-48 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-500 transition"
            onClick={() => navigate("/msmeTube")}
          >
            Watch Videos
          </button>
          
          <button
            className="ml-2 h-16 lg:h-10 px-1 text-xs lg:text-sm w-48 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-500 transition"
            onClick={() => navigate("/podcast-mylist")}
          >
            Podcast
          </button>

          <button
            className="ml-2 h-16 lg:h-10 px-1 text-xs lg:text-sm w-72 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-500 transition"
            onClick={() => navigate("/digiboutique")}
          >
            Contact Think5Whys Boutique Advisor
          </button>

          <button
            className="ml-2 h-16 lg:h-10 px-1 text-xs lg:text-sm w-48 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-500 transition"
            onClick={() => navigate("/think5whys-consultants")}
          >
            Contact Expert
          </button>
        </div>

        <p className="text-xs lg:text-base mt-4 mb-4">
          This platform helps users estimate tasks using the 
          <span className="font-semibold"> Three-Point Estimation (3P)</span> method.
          Instead of guessing a single value, users estimate using a realistic
          range of outcomes to improve planning and reduce estimation errors.
        </p>

        <p className="text-xs lg:text-base mb-6">
          The platform combines estimation mathematics, reasoning,
          risk awareness, AI support and visualization to help users
          think more clearly about tasks and planning.
        </p>

        {/* Tabs */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📑 Platform Navigation Tabs
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">
          <p>💰 <span className="font-semibold">3P New</span> – Create a new estimation task.</p>
          <p>📚 <span className="font-semibold">My 3Ps</span> – Review your previous estimations.</p>
          <p>⚡ <span className="font-semibold">About</span> – Learn the theory behind Three-Point Estimation.</p>
          <p>📅 <span className="font-semibold">Use-Cases</span> – See real world estimation examples.</p>
          <p>🧭 <span className="font-semibold">Help</span> – Read this complete user guide.</p>
          <p>🎯 <span className="font-semibold">Contest</span> – Participate in estimation challenges.</p>
        </div>


        {/* Creating estimation */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🆕 Creating a New Estimation
        </h2>

        <p className="text-xs lg:text-base mb-4">
          In the <span className="font-semibold">3P New</span> tab you can create
          a new estimation for any task.
        </p>

        <div className="bg-gray-600 p-4 rounded-lg mb-8 text-xs lg:text-base">

          <p>🔢 A unique <span className="font-semibold">3P Task Code</span> is automatically generated.</p>

          <p>📱 A <span className="font-semibold">QR Code</span> is generated for easy sharing.</p>

          <p>📝 Enter the <span className="font-semibold">Task Title</span>.</p>

          <p>⏱ Select the <span className="font-semibold">Estimation Unit</span> such as Minutes, Hours, Days, Weeks or Months.</p>

        </div>


        {/* Smart features */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🤖 Smart Input Features
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">

          <p>🧠 <span className="font-semibold">Gen AI Support</span> helps analyze your reasoning and generate structured explanations.</p>

          <p>🎤 <span className="font-semibold">Voice Input</span> allows you to speak descriptions instead of typing.</p>

          <p>📝 Users can provide reasoning behind each estimation value.</p>

        </div>


        {/* Three point inputs */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🔢 Three-Point Estimation Inputs
        </h2>

        <p className="text-xs lg:text-base mb-4">
          The Three-Point Estimation method uses three values to represent
          a realistic range of possible outcomes for a task.
        </p>


        {/* Pessimistic */}
        <div className="mb-6 bg-gray-700 p-4 rounded-lg">

          <p className="text-sm lg:text-lg font-semibold text-red-400 mb-2">
            🔴 P – Pessimistic Estimate
          </p>

          <p className="text-xs lg:text-base mb-2">
            Represents the <span className="font-semibold">worst case scenario</span>.
          </p>

          <p className="text-xs lg:text-base mb-3 italic">
            Question to ask:  
            “If everything goes wrong, how long could this task take?”
          </p>

          <p className="text-xs lg:text-base font-semibold mb-1">Examples:</p>

          <div className="text-xs lg:text-base space-y-1 mb-3">
            <p>• Software debugging delays</p>
            <p>• Bad weather in agriculture</p>
            <p>• Machine breakdown in factory</p>
          </div>

          <p className="text-xs lg:text-base font-semibold mb-1">
            Pessimistic Details
          </p>

          <p className="text-xs lg:text-base mb-2">
            Explain why the estimate could become large.
          </p>

          <div className="text-xs lg:text-base space-y-1">
            <p>• Server issues may delay deployment</p>
            <p>• External approvals required</p>
            <p>• Raw material delivery delays possible</p>
          </div>

        </div>


        {/* Optimistic */}
        <div className="mb-6 bg-gray-700 p-4 rounded-lg">

          <p className="text-sm lg:text-lg font-semibold text-green-400 mb-2">
            🟢 O – Optimistic Estimate
          </p>

          <p className="text-xs lg:text-base mb-2">
            Represents the <span className="font-semibold">best case scenario</span>.
          </p>

          <p className="text-xs lg:text-base mb-3 italic">
            Question to ask:  
            “If everything goes perfectly, what is the shortest completion time?”
          </p>

          <p className="text-xs lg:text-base font-semibold mb-1">Examples:</p>

          <div className="text-xs lg:text-base space-y-1 mb-3">
            <p>• Team already prepared</p>
            <p>• No external dependencies</p>
            <p>• All materials available</p>
          </div>

          <p className="text-xs lg:text-base">
            You can add <span className="font-semibold">Optimistic Details</span> to explain
            the assumptions behind the best case estimate.
          </p>

        </div>


        {/* Most Likely */}
        <div className="mb-8 bg-gray-700 p-4 rounded-lg">

          <p className="text-sm lg:text-lg font-semibold text-yellow-300 mb-2">
            🟡 M – Most Likely Estimate
          </p>

          <p className="text-xs lg:text-base mb-2">
            Represents the <span className="font-semibold">realistic expected duration</span>
            under normal conditions.
          </p>

          <p className="text-xs lg:text-base mb-3 italic">
            Question to ask:  
            “Under normal conditions, how long will this task take?”
          </p>

          <p className="text-xs lg:text-base font-semibold mb-1">Examples:</p>

          <div className="text-xs lg:text-base space-y-1 mb-3">
            <p>• Typical working conditions</p>
            <p>• Normal productivity</p>
            <p>• No major risks</p>
          </div>

          <p className="text-xs lg:text-base">
            You can add <span className="font-semibold">Most Likely Details</span> to explain
            the reasoning behind this estimate.
          </p>

        </div>


        {/* Example */}
        <div className="bg-gray-600 p-4 rounded-lg mb-8">

          <h3 className="font-semibold mb-2 text-sm lg:text-lg">
            💻 Example Estimation
          </h3>

          <div className="text-xs lg:text-base">
            <p>Optimistic (O): 4 days</p>
            <p>Most Likely (M): 7 days</p>
            <p>Pessimistic (P): 16 days</p>
          </div>

        </div>


        {/* Additional task fields */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📋 Additional Task Details
        </h2>

        <p className="text-xs lg:text-base mb-4">
          You can optionally enable <span className="font-semibold">Input Additional Details</span>
          to record extended information about the task.
        </p>

        <div className="text-xs lg:text-base space-y-1 mb-8">

          <p>👤 Person Think5Whys Code</p>
          <p>🧾 Master Task Code</p>
          <p>📂 Task Group / Category</p>
          <p>📁 Project</p>
          <p>📊 Program</p>
          <p>🏢 Entity / Organisation</p>
          <p>🏛 Department</p>
          <p>🆔 Job ID</p>
          <p>📄 Task Description</p>
          <p>💰 Estimated Cost</p>

        </div>


        {/* Calculation */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📊 Estimation Calculation Methods
        </h2>

        <div className="mb-8">

          <h3 className="text-sm lg:text-lg font-semibold text-blue-700 mb-2">
            A. Triangular Distribution
          </h3>

          <p className="text-xs lg:text-base mb-2">
            Formula: <span className="font-mono">(O + M + P) / 3</span>
          </p>

          <p className="text-xs lg:text-base">
            This is a simple average used for quick estimation.
          </p>

        </div>


        <div className="mb-8">

          <h3 className="text-sm lg:text-lg font-semibold text-green-700 mb-2">
            B. PERT Estimate (Weighted)
          </h3>

          <p className="text-xs lg:text-base mb-2">
            Formula: <span className="font-mono">(O + 4M + P) / 6</span>
          </p>

          <p className="text-xs lg:text-base">
            This method gives greater weight to the
            <span className="font-semibold"> Most Likely estimate</span>.
          </p>

        </div>


        {/* SD */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📉 Standard Deviation
        </h2>

        <p className="text-xs lg:text-base mb-4">
          Standard deviation measures uncertainty in the estimate.
        </p>

        <p className="text-xs lg:text-base mb-8">
          Formula: <span className="font-mono">(P − O) / 6</span>
        </p>


        {/* Estimation card */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📋 Estimation Card
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">

          <p>Click <span className="font-semibold">Show Estimation Card</span> to generate a visual summary.</p>

          <p>The card includes:</p>

          <p>📊 Estimation distribution chart</p>
          <p>📋 Parameter table</p>
          <p>📝 Task descriptions and remarks</p>
          <p>📉 Expected value and standard deviation</p>

        </div>


        {/* Export */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📄 Export & Storage
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">

          <p>📄 Download estimation results as a PDF report.</p>

          <p>💾 Future versions will allow saving tasks in the Chronicle database.</p>

        </div>


        {/* Best practices */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          ✅ Estimation Best Practices
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">

          <p>✔ Always explain reasoning behind estimates.</p>

          <p>✔ Consider risks and uncertainties.</p>

          <p>✔ Avoid unrealistic optimism.</p>

          <p>✔ Compare with previous similar tasks.</p>

          <p>✔ Use ranges instead of single guesses.</p>

        </div>


        {/* Closing */}
        <div className="bg-gray-700 p-4 rounded-lg">

          <p className="text-xs lg:text-base font-semibold">
            Think5Whys builds structured thinking tools for estimation,
            decision making and problem solving.
          </p>

          <p className="text-xs lg:text-base mt-2">
            Estimate → Understand Uncertainty → Plan Better → Execute Better
          </p>

        </div>

      </div>
    </div>
  );
};

export default ThreePHelp;